<template>
  <div
    v-if="loading"
    class="skeleton flex w-full items-center rounded-md border h-10 px-4 focus-within:border-secondary-500 focus-within:shadow-[0px_0px_4px_theme('colors.primary.500')]"
  />
  <select
    v-else
    ref="selectElement"
    :name="name"
    class="h-fit w-full rounded-md border border-neutral-300 bg-neutral-0 px-4 text-neutral-800 outline-none transition-colors required:invalid:text-neutral-300 hover:border-secondary-500 focus:border-secondary-500 focus:shadow-[0px_0px_4px_theme('colors.primary.500')] focus:ring-0 active:border-secondary-500 disabled:border-neutral-400 disabled:bg-neutral-50"
    :class="[
      size === 'small' ? 'py-2 text-xs' : '',
      size === 'medium' ? 'py-4 text-sm' : '',
      size === 'large' ? 'py-4 text-base' : ''
    ]"
    :disabled="disabled"
    :required="required"
    @change="emit('update:modelValue', $event.target?.value)"
  >
    <option value="" disabled hidden selected>{{ labelWithDefault }}</option>

    <slot>
      <option
        v-for="(option, i) in options"
        :key="`option-${typeof option === 'string' ? i : option.key}`"
        :value="typeof option === 'string' ? option : option.value"
        :selected="typeof option === 'string' ? option == modelValue : option.value == modelValue"
      >
        {{
          format(typeof option === 'string' ? option : option.key) || (typeof option === 'string' ? option : option.key)
        }}
      </option>
    </slot>
  </select>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
  modelValue: any;
  options: string[] | { key: string; value: any }[];
  name: string;
  label?: string;
  size?: 'small' | 'medium' | 'large';
  format?: (value: any) => string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  format: () => '',
  required: true,
  disabled: false,
  loading: false
});

const emit = defineEmits(['update:modelValue', 'change']);

const selectElement = ref<HTMLSelectElement>();

const focus = () => selectElement.value?.focus();

const labelWithDefault = props.label === undefined ? t('messageType.select') : props.label;

defineExpose({ focus });
</script>

<i18n>
{
  "en": {
    "messageType": {
      "select": "Please select..."
    }
  },
  "nl": {
    "messageType": {
      "select": "Selecteer..."
    }
  }
}
</i18n>
